import {ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {ICellEditorParams} from 'ag-grid-community';
import {Observable, Subject} from 'rxjs';
import {FormControl} from '@angular/forms';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {GridUtils} from '../../../../shared-utilities/utils-old/grid-utils-old/grid-utils';
import {selectSuppliersByUserSelectedStore} from '../../../../features/ngp-report/store/ngp-report.selectors';
import {selectSuppliersForUserSelectedStore} from '../../../../features/stock-manager/store/stock-manager.selectors';
import {ISearchableFields} from '../../../../shared/shared-models/type-sense/default-searchable-fields';
import {StockItem, Supplier} from '../../../../shared-utilities/models-old/datastructures';
import {NGPReport} from '../../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {selectCurrentNavigationPage} from '../../../../features/user-access/user-stores-store/user-store.selectors';
import {IconsEnum} from '../../../shared-icons/models/icons.enum';
import {checkSupplierCodeAutoFillOnSupplierChange} from '../../../../features/stock-manager/store/stock-manager.actions';
import {stockManagerHasEditableFieldChanges, STOCK_MANAGER_BLANK_FIELD_DISPLAY} from '../../../../features/stock-manager/utils/stock-manager-update.utils';

@Component({
  selector: 'app-cell-editor-supplier',
  templateUrl: './cell-editor-supplier.component.html',
  styleUrls: ['./cell-editor-supplier.component.scss'],
  standalone: false,
})
export class CellEditorSupplierComponent implements ICellEditorAngularComp, OnDestroy {

  @ViewChild('input') input: ElementRef;

  value: string | number;
  params: ICellEditorParams<StockItem | NGPReport>;
  gridData: StockItem | NGPReport;
  width: number;
  colDefField: string;
  departments = {};
  unSortedSuppliers: { key: string; description: string; }[];
  currentPage: string;
  searchControl = new FormControl('');
  filteredOptions: { key: string; description: string; }[] = [];

  currentPage$: Observable<string>;
  unSortedSuppliers$: Observable<Record<string, Supplier> | ISearchableFields>;

  protected readonly icons: typeof IconsEnum = IconsEnum;
  protected readonly blankSupplierLabel = STOCK_MANAGER_BLANK_FIELD_DISPLAY;
  protected readonly decodeURIComponent = decodeURIComponent;

  private destroy$ = new Subject<boolean>();
  /** Set when delete/clear commits so getValue() is not overwritten by the select ngModel. */
  private explicitCommitValue: string | undefined;
  /** True after the user clears the supplier; kept until the edit is committed. */
  private isSupplierCleared = false;
  private initialSupplierCode = '';
  /** Last valid supplier in this edit session (includes blank after clear). */
  private lastValidEditorSupplierCode = '';
  /** Prevents duplicate auto-fill prompts when selection + getValue both commit. */
  private autoFillDispatchedForSupplierCode: string | null = null;

  constructor(
    private readonly store: Store,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  agInit(params: ICellEditorParams): void {
    if (Object.isFrozen(params.node.data)) {
      const mutableCopy = structuredClone(params.node.data);
      params.node.data = mutableCopy;
      params.data = mutableCopy;
    }

    params.api.setColumnWidths([
      {key: params.colDef.field, newWidth: 400},
    ]);

    this.params = {...params};
    this.gridData = structuredClone(params.data as StockItem | NGPReport);
    this.colDefField = params.colDef.field;
    this.value = this.params.data[this.colDefField];
    this.initialSupplierCode = this.normalizeSupplierCode(String(this.value ?? ''));
    this.lastValidEditorSupplierCode = this.initialSupplierCode;
    this.autoFillDispatchedForSupplierCode = null;
    this.width = this.params.colDef.width;
    this.gridData.originalValue = {...params.data.originalValue};
    this.gridData.originalValue[this.colDefField] = {...params.data.originalValue[this.colDefField]};

    this.currentPage$ = this.store.select(selectCurrentNavigationPage);
    this.currentPage$.pipe(takeUntil(this.destroy$)).subscribe((page: string) => {
      this.currentPage = page;
    });
    if (this.currentPage === 'ngp-report') {
      this.unSortedSuppliers$ = this.store.select(selectSuppliersByUserSelectedStore);
    } else {
      this.unSortedSuppliers$ = this.store.select(selectSuppliersForUserSelectedStore);
    }
    this.unSortedSuppliers$.pipe(takeUntil(this.destroy$)).subscribe((suppliers: ISearchableFields | Record<string, Supplier>): void => {
      this.unSortedSuppliers = this.objectToArray(suppliers);
      this.filterOptions(String(this.searchControl.value ?? ''));
      this.updateSearchControlDisplay();
      this.cdr.markForCheck();
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(200),
    ).pipe(takeUntil(this.destroy$)).subscribe((searchTerm: string): void => {
      this.filterOptions(searchTerm);
    });

    params.api.setColumnWidths([
      {key: params.colDef.field, newWidth: 350},
    ]);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  objectToArray(obj: ISearchableFields | Record<string, Supplier>): { key: string; description: string; }[] {
    if (!obj) {
      return [];
    }
    return Object.keys(obj).map((key: string) => ({key, description: obj[key].name}));
  }

  onSearchInput(event: Event): void {
    const searchTerm = String((event.target as HTMLInputElement).value ?? '');
    if (searchTerm && searchTerm !== STOCK_MANAGER_BLANK_FIELD_DISPLAY) {
      this.isSupplierCleared = false;
      if (this.explicitCommitValue === '') {
        this.explicitCommitValue = undefined;
      }
    }
    this.filterOptions(searchTerm);
  }

  filterOptions(searchTerm: string): void {
    const suppliers = this.unSortedSuppliers ?? [];
    const trimmedSearchTerm = String(searchTerm ?? '').trim();
    if (!trimmedSearchTerm) {
      this.filteredOptions = [...suppliers];
      this.cdr.markForCheck();
      return;
    }

    const lowerSearchTerm = trimmedSearchTerm.toLowerCase();
    this.filteredOptions = suppliers.filter((option: { key: string; description: string }) => {
      const keyMatches = option.key.toLowerCase().includes(lowerSearchTerm);
      const descriptionMatches = option.description?.toLowerCase().includes(lowerSearchTerm);
      const displayMatches = `${option.key} - ${option.description}`.toLowerCase().includes(lowerSearchTerm);
      return keyMatches || descriptionMatches || displayMatches;
    });
    this.cdr.markForCheck();
  }

  onEnterKey(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.shouldCommitBlankSupplier()) {
      this.applyBlankSupplierSelection(false);
      this.commitEditAndNavigateDown();
      return;
    }

    const inputElement = event.target as HTMLInputElement;
    const resolvedSupplier = this.resolveSupplierForCommit(inputElement.value);

    if (resolvedSupplier === null) {
      this.revertToLastValidSupplier();
      this.explicitCommitValue = this.getLastValidSupplierCode();
      this.commitEditAndNavigateDown();
      return;
    }

    if (resolvedSupplier === '') {
      this.applyBlankSupplierSelection(false);
    } else {
      this.explicitCommitValue = resolvedSupplier;
      this.applySupplierSelection(resolvedSupplier);
    }
    this.commitEditAndNavigateDown();
  }

  onTabKey(event: Event): void {
    if (this.shouldCommitBlankSupplier()) {
      this.applyBlankSupplierSelection(false);
      return;
    }

    const inputElement = event.target as HTMLInputElement;
    const resolvedSupplier = this.resolveSupplierForCommit(inputElement.value);

    if (resolvedSupplier === null) {
      this.revertToLastValidSupplier();
      return;
    }

    if (resolvedSupplier === '') {
      this.applyBlankSupplierSelection(false);
      return;
    }

    this.explicitCommitValue = resolvedSupplier;
    this.applySupplierSelection(resolvedSupplier);
  }

  updateSearchControlDisplay(): void {
    if (this.value && this.unSortedSuppliers?.length > 0) {
      const supplier = this.unSortedSuppliers.find((s: { key: string; description: string }) => s.key === this.value);
      if (supplier) {
        this.searchControl.setValue(`${supplier.key} - ${supplier.description}`, {emitEvent: false});
      }
    }
  }

  onSelectionChange(event: Event): void {
    const selectedCode = String((event.target as HTMLSelectElement).value ?? '').trim();
    this.onSelectModelChange(selectedCode);
  }

  onSelectModelChange(selectedCode: string): void {
    const normalizedSelectedCode = String(selectedCode ?? '').trim();
    if (normalizedSelectedCode === '') {
      this.applyBlankSupplierSelection(false);
      return;
    }

    const normalizedCode = this.normalizeSupplierCode(normalizedSelectedCode);
    if (!normalizedCode) {
      this.revertToLastValidSupplier();
      return;
    }

    this.applySupplierSelection(normalizedCode);
  }

  getSelectDisplayCode(): string {
    if (this.shouldCommitBlankSupplier()) {
      return '';
    }

    const searchTerm = String(this.searchControl.value ?? '').trim();
    if (searchTerm.length > 0 && this.filteredOptions?.length > 0) {
      return this.filteredOptions[0].key;
    }
    return String(this.value ?? '');
  }

  onUndoChangesClick(): void {
    this.value = this.params.data.originalValue[this.colDefField].value as string;
    this.gridData[this.colDefField] = this.value;
    this.isSupplierCleared = false;
    this.autoFillDispatchedForSupplierCode = null;
    this.lastValidEditorSupplierCode = this.normalizeSupplierCode(String(this.value ?? ''));
    this.updateSearchControlDisplay();
    GridUtils.removeIsEditingFlagAndDispatch(this.gridData, this.colDefField, this.store, this.currentPage);
  }

  onIconPointerDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onClearSupplierPointerDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.applyBlankSupplierSelection(true);
    setTimeout((): void => {
      this.input?.nativeElement?.focus();
    }, 0);
  }

  onClearSupplierClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.input?.nativeElement?.focus();
  }

  hasSupplierValue(): boolean {
    return String(this.value ?? '').trim().length > 0;
  }

  hasSupplierChanged(): boolean {
    const original = String(this.params.data.originalValue[this.colDefField].value ?? '').trim();
    return String(this.value ?? '').trim() !== original;
  }

  clearInput(): void {
    if (this.isSupplierCleared) {
      return;
    }
    this.searchControl.setValue('', {emitEvent: false});
    this.filterOptions('');
  }

  getValue(): string {
    if (this.shouldCommitBlankSupplier()) {
      this.isSupplierCleared = false;
      this.explicitCommitValue = undefined;
      this.commitSupplierChange('');
      return '';
    }

    const supplierCode = this.explicitCommitValue !== undefined
      ? String(this.explicitCommitValue).trim()
      : String(this.value ?? '').trim();
    this.explicitCommitValue = undefined;
    const validatedSupplierCode = this.validateSupplierCodeForCommit(supplierCode);
    this.commitSupplierChange(validatedSupplierCode);
    return validatedSupplierCode;
  }

  private validateSupplierCodeForCommit(supplierCode: string): string {
    if (this.isSupplierCleared || supplierCode === '') {
      return '';
    }
    const normalizedCode = this.normalizeSupplierCode(supplierCode);
    if (normalizedCode) {
      return normalizedCode;
    }
    return this.getLastValidSupplierCode();
  }

  private resolveSupplierForCommit(inputValue: string): string | null {
    if (this.shouldCommitBlankSupplier()) {
      return '';
    }

    const trimmedInput = String(inputValue ?? '').trim();
    if (trimmedInput === STOCK_MANAGER_BLANK_FIELD_DISPLAY) {
      return '';
    }

    if (!trimmedInput) {
      if (this.isBlankSupplierSelection()) {
        return '';
      }
      const selectedCode = String(this.value ?? '').trim();
      if (selectedCode === '') {
        return '';
      }
      return this.normalizeSupplierCode(selectedCode) || null;
    }

    const exactMatch = this.resolveExactSupplierFromInput(trimmedInput);
    if (exactMatch) {
      return exactMatch;
    }

    if (this.filteredOptions?.length > 0) {
      return this.filteredOptions[0].key;
    }

    return null;
  }

  private resolveExactSupplierFromInput(inputValue: string): string | null {
    if (!this.unSortedSuppliers?.length) {
      return null;
    }

    const supplierCodeFromInput = this.extractSupplierSearchTerm(inputValue);
    const lowerInput = inputValue.toLowerCase();
    const lowerCode = supplierCodeFromInput.toLowerCase();

    const exactMatch = this.unSortedSuppliers.find((option: { key: string; description: string }) =>
      option.key.toLowerCase() === lowerCode ||
      option.key.toLowerCase() === lowerInput ||
      `${option.key} - ${option.description}`.toLowerCase() === lowerInput,
    );

    return exactMatch?.key ?? null;
  }

  private normalizeSupplierCode(code: string): string {
    return this.resolveExactSupplierFromInput(String(code ?? '').trim()) ?? '';
  }

  private getLastValidSupplierCode(): string {
    const currentCode = this.normalizeSupplierCode(String(this.value ?? ''));
    if (currentCode) {
      return currentCode;
    }
    return this.lastValidEditorSupplierCode;
  }

  private revertToLastValidSupplier(): void {
    const validCode = this.getLastValidSupplierCode();
    this.isSupplierCleared = validCode === '';
    this.value = validCode;
    this.gridData[this.colDefField] = validCode;
    if (validCode === '') {
      this.searchControl.setValue('', {emitEvent: false});
      return;
    }
    this.updateSearchControlDisplay();
  }

  private applySupplierSelection(supplierCode: string): void {
    const normalizedCode = this.normalizeSupplierCode(supplierCode);
    if (!normalizedCode) {
      this.revertToLastValidSupplier();
      return;
    }

    this.isSupplierCleared = false;
    this.value = normalizedCode;
    this.gridData[this.colDefField] = normalizedCode;
    this.lastValidEditorSupplierCode = normalizedCode;
    this.updateSearchControlDisplay();
    const stockItemForCommit = this.buildStockItemForCommit(normalizedCode);
    const previousSupplierCode = String(stockItemForCommit.originalValue?.[this.colDefField]?.value ?? '').trim();
    GridUtils.updateSingleItem(stockItemForCommit, this.colDefField, normalizedCode, this.store, this.currentPage);
    this.applyEditedStateToRow(normalizedCode, stockItemForCommit);
    this.maybeDispatchSupplierCodeAutoFill(stockItemForCommit as StockItem, normalizedCode, previousSupplierCode);
  }

  private commitSupplierChange(supplierCode: string): void {
    this.value = supplierCode;
    this.gridData[this.colDefField] = supplierCode;

    const stockItemForCommit = this.buildStockItemForCommit(supplierCode);
    const previousSupplierCode = String(stockItemForCommit.originalValue?.[this.colDefField]?.value ?? '').trim();
    GridUtils.updateSingleItem(stockItemForCommit, this.colDefField, supplierCode, this.store, this.currentPage);
    this.applyEditedStateToRow(supplierCode, stockItemForCommit);
    this.maybeDispatchSupplierCodeAutoFill(stockItemForCommit as StockItem, supplierCode, previousSupplierCode);
  }

  /**
   * Dispatches the auto-fill check when Regular Supplier changes on Stock Manager.
   * Called from both selection apply and getValue commit because Enter cancels editing
   * (stopEditing(true)) and skips getValue.
   */
  private maybeDispatchSupplierCodeAutoFill(
    stockItem: StockItem,
    newSupplierCode: string,
    previousSupplierCode: string,
  ): void {
    if (this.currentPage === 'ngp-report' || this.colDefField !== 'regularSuppCode') {
      return;
    }

    const normalizedNewCode = String(newSupplierCode ?? '').trim();
    if (!normalizedNewCode || normalizedNewCode === previousSupplierCode) {
      return;
    }

    if (this.autoFillDispatchedForSupplierCode === normalizedNewCode) {
      return;
    }

    this.autoFillDispatchedForSupplierCode = normalizedNewCode;
    this.store.dispatch(checkSupplierCodeAutoFillOnSupplierChange({
      stockItem,
      newSupplierCode: normalizedNewCode,
      previousSupplierCode,
    }));
  }

  private buildStockItemForCommit(supplierCode: string): StockItem | NGPReport {
    const rowData = this.params.node?.data ?? this.params.data;
    return {
      ...rowData,
      [this.colDefField]: supplierCode,
    } as StockItem | NGPReport;
  }

  private applyEditedStateToRow(supplierCode: string, stockItemForCommit?: StockItem | NGPReport): void {
    const rowNode = this.params.node;
    if (!rowNode?.data) {
      return;
    }

    const mergedData = stockItemForCommit ?? this.buildStockItemForCommit(supplierCode);
    const isStockManagerGrid = Boolean((this.params.context as { isStockManager?: boolean } | undefined)?.isStockManager);
    const isAnyEdited = isStockManagerGrid
      ? stockManagerHasEditableFieldChanges(mergedData as StockItem)
      : mergedData[this.colDefField] !== mergedData.originalValue?.[this.colDefField]?.value;

    const updatedData = {
      ...mergedData,
      isEdited: isAnyEdited,
    };
    rowNode.setData(updatedData);
    this.params.data = updatedData;
    this.gridData.isEdited = isAnyEdited;
    this.params.api.refreshCells({
      rowNodes: [rowNode],
      columns: [this.colDefField],
      force: true,
    });
  }

  private extractSupplierSearchTerm(searchTerm: string): string {
    return String(searchTerm ?? '').split(' - ')[0]?.trim() ?? '';
  }

  private isBlankSupplierSelection(): boolean {
    return String(this.value ?? '').trim() === ''
      && this.isBlankSupplierSearchTerm(String(this.searchControl.value ?? '').trim());
  }

  private isBlankSupplierSearchTerm(searchTerm: string): boolean {
    return searchTerm === '' || searchTerm === STOCK_MANAGER_BLANK_FIELD_DISPLAY;
  }

  private shouldCommitBlankSupplier(): boolean {
    if (this.isSupplierCleared) {
      return true;
    }

    const searchTerm = String(this.searchControl.value ?? '').trim();
    if (this.isBlankSupplierSearchTerm(searchTerm) && String(this.value ?? '').trim() === '') {
      return true;
    }

    return this.isBlankSupplierSelection();
  }

  private applyBlankSupplierSelection(persistImmediately: boolean): void {
    this.isSupplierCleared = true;
    this.explicitCommitValue = '';
    this.value = '';
    this.gridData[this.colDefField] = '';
    this.lastValidEditorSupplierCode = '';
    this.searchControl.setValue(STOCK_MANAGER_BLANK_FIELD_DISPLAY, {emitEvent: false});
    this.filterOptions('');

    if (persistImmediately) {
      const stockItemForCommit = this.buildStockItemForCommit('');
      GridUtils.updateSingleItem(stockItemForCommit, this.colDefField, '', this.store, this.currentPage);
      this.applyEditedStateToRow('', stockItemForCommit);
    }

    this.cdr.markForCheck();
  }

  private commitEditAndNavigateDown(): void {
    const gridApi = this.params.api;
    const rowIndex = this.params.node.rowIndex;
    const colKey = this.params.column.getColId();

    gridApi.stopEditing(true);

    setTimeout((): void => {
      this.navigateToNextEnabledRow(rowIndex, colKey);
    }, 0);
  }

  private navigateToNextEnabledRow(fromRowIndex: number, colKey: string): void {
    const gridApi = this.params.api;
    if (gridApi.isDestroyed()) {
      return;
    }

    let rowIndex = fromRowIndex + 1;
    const maxIterations = 50;
    let iterations = 0;

    while (iterations < maxIterations) {
      const rowNode = gridApi.getDisplayedRowAtIndex(rowIndex);
      const skipDisabledRow = this.currentPage === 'ngp-report' && !!rowNode?.data?._tags?.includes('disabled');
      if (rowNode && !skipDisabledRow) {
        gridApi.startEditingCell({rowIndex, colKey});
        return;
      }
      rowIndex++;
      iterations++;
      if (rowIndex >= gridApi.getDisplayedRowCount()) {
        break;
      }
    }
  }

  refresh(params: ICellEditorParams): boolean {
    GridUtils.updateIsEditedAndForceRefresh(this.params);
    return false;
  }

  afterGuiAttached(): void {
    if (this.currentPage !== 'ngp-report' || !this.params?.data?._tags?.includes('disabled')) {
      this.input.nativeElement.focus();
    }
  }

}
